<template>
    <div class="LinkedInProfileButton flex w-full flex-col overflow-hidden rounded-lg border border-gray-450 dark:border-default">
        <article class="flex w-full items-center justify-between gap-4 bg-white dark:bg-surface-card p-3.5">
            <div class="flex h-full items-center gap-3.5">
                <img
                    alt="LinkedIn logo"
                    class="block aspect-square size-9 shrink-0"
                    :height="36"
                    src="@/assets/images/logos/linkedin.png"
                    :width="36"
                />

                <header class="flex flex-col gap-1.5">
                    <h3 class="text-md font-medium leading-none">LinkedIn prepojenie</h3>
                    <small class="text-xs leading-none">Synchronizácia údajov z LinkedIn účtu</small>
                </header>
            </div>

            <div class="flex h-full items-center gap-3.5">
                <div class="mx-1 h-1/2 w-px bg-gray-450 dark:bg-subtle" />

                <!-- Initial check in flight: show a neutral loading state instead of a
                     (possibly wrong) "Prepojiť", until we know the real connection status. -->
                <div
                    v-if="isCheckingConnection"
                    class="flex items-center gap-2 self-center py-1.5 pr-1 text-xs font-medium uppercase text-gray-400 dark:text-txt-body"
                >
                    <CSpinner class="size-4" />
                    <span>Načítavam</span>
                </div>
                <CButton
                    v-else-if="isAuthorized"
                    class="min-h-0! self-center py-1.5! pl-2! pr-3! text-xs font-medium uppercase"
                    :disabled="isDisabled"
                    variant="default"
                    @click="handleAuthorization"
                >
                    <template #icon>
                        <CIcon class="size-4 rotate-90" name="preferences" />
                    </template>
                    <span class="-ml-1">Upraviť</span>
                </CButton>
                <CButton
                    v-else
                    class="min-h-0! self-center py-1.5! pl-2! pr-3! text-xs font-medium uppercase"
                    :disabled="isDisabled"
                    :loading="connectingState === 'loading'"
                    variant="primary"
                    @click="handleAuthorization"
                >
                    <template #icon>
                        <CIcon class="size-4" name="link" />
                    </template>
                    <span v-if="connectingState === 'loading'" class="-ml-1">Prepájam</span>
                    <span v-else class="-ml-1">Prepojiť</span>
                </CButton>
            </div>
        </article>

        <CButton
            v-if="isAuthorized"
            class="min-h-0! w-full rounded-none! p-2.5! hover:rounded-none! hover:no-underline!"
            :class="isHovered ? '[&]:bg-red-100!' : '[&]:bg-[#F2F7EE]!'"
            :disabled="isDisabled"
            :loading="deactivationState === 'loading'"
            variant="transparent"
            @click="handleDeactivate"
            @mouseleave="isHovered = false"
            @mouseover="isHovered = true"
        >
            <div class="flex items-center justify-between gap-1" :class="isHovered ? 'text-red-700 dark:text-red-300' : 'text-[#5A8949]'">
                <CIcon
                    class="block size-4 stroke-current stroke-[0.5px] text-current"
                    :name="isHovered ? 'refuse' : 'check-circle-broken'"
                />
                <small class="block text-sm font-medium text-current">
                    <template v-if="isHovered">Deaktivovať</template>
                    <template v-else>Aktívne</template>
                </small>
            </div>
        </CButton>
    </div>
</template>

<script setup lang="ts">
import { EMemberSnapshotDataDomains } from '#shared/types/LinkedIn'
import type { LoadingState } from '#shared/types/LoadingState'
import { useLinkedin } from '@/composables/linkedin/useLinkedin'
import { useLinkedInRedirectUri } from '@/composables/linkedin/useLinkedInRedirectUri'
import type { PropType } from 'vue'
import { computed, onMounted, ref } from 'vue'
import { useModal } from 'vue-final-modal'
import LinkedInAuthorizeModal from './modals/LinkedInAuthorizeModal.vue'

const props = defineProps({
    connectionData: {
        type: Object as PropType<Awaited<ReturnType<typeof getConnectionData>> | null>,
        default: null
    },
    profileData: {
        type: Object as PropType<Awaited<ReturnType<typeof getProfileData>> | null>,
        default: null
    },
    disabled: {
        type: Boolean,
        default: false
    },
    state: {
        type: String as PropType<LoadingState>,
        default: 'idle'
    }
})

const emit = defineEmits(['update:connection-data', 'update:profile-data', 'update:state'])

const route = useRoute()
const router = useRouter()
const { getAuthorizationLink, getConnectionData, getProfileData, deleteConnectionData } = useLinkedin()
const { getLinkedInRedirectUri } = useLinkedInRedirectUri()

const isHovered = ref(false)
let redirectUri = ''

// State for different loading states
const connectingState = ref<LoadingState>('idle')
// Starts 'loading': the connection check always runs on mount, so we render the
// neutral loading state first rather than briefly flashing a wrong "Prepojiť".
const loadingState = ref<LoadingState>('loading')
const deactivationState = ref<LoadingState>('idle')

const isAuthorized = computed(() => {
    return !!props.connectionData && !!props.connectionData.domains && props.connectionData.domains.length > 0
})

// True only during the initial connection check, before we know the real status.
const isCheckingConnection = computed(() => {
    return loadingState.value === 'loading' && !props.connectionData
})

const isDisabled = computed(() => {
    return (
        props.disabled ||
        props.state === 'loading' ||
        connectingState.value === 'loading' ||
        loadingState.value === 'loading' ||
        deactivationState.value === 'loading'
    )
})

onMounted(async () => {
    if (import.meta.client) {
        redirectUri = getLinkedInRedirectUri()

        const code = route.query.code as string | undefined
        const state = route.query.state as string | undefined

        await loadConnectionData()

        if (code && state) {
            setConnectingState('loading')

            await loadProfileData(code, state)
            await loadConnectionData()

            // Navigate to the base path without query parameters
            const cleanPath = route.path.replace('/undefined', '') || route.path
            router.replace(cleanPath)
            setConnectingState('idle')

            return
        }

        if (props.connectionData) {
            await loadProfileData()
        }
    }
})

async function loadConnectionData() {
    setLoadingState('loading')

    try {
        const connectionData = await getConnectionData()
        emit('update:connection-data', connectionData)
        setLoadingState('idle')
    } catch (error: any) {
        emit('update:connection-data', null)
        emit('update:profile-data', null)

        // 404 = user simply has no LinkedIn connection yet — that's the normal
        // not-connected state, not an error. Only surface genuine failures.
        if (error?.response?.status === 404) {
            setLoadingState('idle')
        } else {
            console.error('Error loading LinkedIn connection data:', error?.response?.data || error?.message)
            setLoadingState('error')
        }
    }
}

async function loadProfileData(code?: string, state?: string) {
    setLoadingState('loading')

    try {
        const profileData = await getProfileData(redirectUri, code, state)
        emit('update:profile-data', profileData)
        setLoadingState('idle')
    } catch (error: any) {
        console.error('Error loading LinkedIn profile data:', error?.response?.data || error?.message)
        emit('update:profile-data', null)
        setLoadingState('error')
    }
}

async function handleDeactivate() {
    setDeactivationState('loading')

    try {
        await deleteConnectionData()
        emit('update:connection-data', null)
        emit('update:profile-data', null)
        setDeactivationState('idle')
    } catch (error) {
        setDeactivationState('error')
    }
}

async function handleAuthorize(domains: EMemberSnapshotDataDomains[]) {
    const authorizationLink = await getAuthorizationLink(domains, redirectUri)
    if (import.meta.client) {
        // Same-tab redirect (matches /u): the callback on /u consumes the stored
        // returnTo and navigates the user back to where they started.
        window.location.href = authorizationLink.url
    }
}

// Modal setup
const { open: openLinkedInAuthorizeModal, close: closeLinkedInAuthorizeModal } = useModal({
    component: LinkedInAuthorizeModal,
    attrs: {
        onSubmit: async (domains: EMemberSnapshotDataDomains[]) => {
            await closeLinkedInAuthorizeModal()
            await handleAuthorize(domains)
        },
        connectionData: computed(() => props.connectionData)
    }
})

async function handleAuthorization() {
    await openLinkedInAuthorizeModal()
}

function setConnectingState(state: LoadingState) {
    connectingState.value = state
    emit('update:state', state)
}

function setLoadingState(state: LoadingState) {
    loadingState.value = state
    emit('update:state', state)
}

function setDeactivationState(state: LoadingState) {
    deactivationState.value = state
    emit('update:state', state)
}
</script>
